import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { Animation } from "../../Common/Animation/Animation";
import { AnimationState } from "../Animation/AnimationState";
import { PropertyAnimation } from "../../Common/Animation/PropertyAnimation";
import { Scene, SceneNode } from "@spi/Common/Collada";

import { Context } from "../Engine";
import { toRadian } from "@spi/Common/Math/math";
import { BoundingBox } from "@spi/Common/Util";
import { Mesh } from "./Mesh";
import { CompositeObjectInteraction, IDrawableObject } from "./ObjectManager";
import { VertexBuffer } from "./VertexBuffer";
import { CompositeShaderSettings, CompositeShader } from "../Shader";
import { ITexture } from "../Texture";

// TODO: split RenderObject in Animated/Static Objects

export class RenderObject implements IDrawableObject {

    public animatedProperties: PropertyAnimation;

    public interaction: CompositeObjectInteraction;

    public mesh: Mesh;
    public pickable: boolean;
    public pickingColor: number;
    public position: vec3;
    public fixedRotation: number;
    public rotation: number;
    public rotationAxis: vec3;
    public scale: vec3;
    public baseColor: vec4;
    public texture: ITexture;

    public frameIndex: number;

    public selected: boolean;
    public hovered: boolean;

    private drawCount: number;
    private loop: number;
    private loopUp: boolean;

    public isAttached: boolean;
    public animation: Animation;
    public animationState: AnimationState;

    public mvMatrix: mat4;
    public bindShapeMatrix: mat4;
    public boneMatrix: mat4;
    public offsetMatrix: mat4;

    public jointAttachments: Map<SceneNode, RenderObject>;

    public skinned: boolean;
    public controllerNode: SceneNode;
    public visualScene: Scene;

    constructor(mesh: Mesh) {
        this.mesh = mesh;

        this.interaction = new CompositeObjectInteraction();

        this.texture = mesh.texture;
        this.baseColor = vec4.fromValues(1, 1, 1, 1);

        this.position = vec3.fromValues(0, 0, 0);
        this.rotation = 0;
        this.fixedRotation = 0;
        this.rotationAxis = vec3.fromValues(0, 0, 1);
        this.setScale(1);

        this.pickable = true;
        this.pickingColor = 0;

        this.loop = Math.random();
        this.loopUp = true;

        this.frameIndex = 0;
        this.drawCount = 0;

        this.skinned = false;
        this.isAttached = false;
        this.animation = null;
        this.animationState = null;

        this.jointAttachments = new Map<SceneNode, RenderObject>();
    }

    public get isAnimated(): boolean {
        return !!(this.animation && this.animation.validAnimation && this.animationState);
    }

    public setScale(scale: number) {
        this.scale = vec3.fromValues(scale, scale, scale);
    }

    public rotateTo(direction: vec3 | vec2): void {
        let rotRad = Math.atan2(direction[0], direction[1]);

        let rotDeg = rotRad * 180 / Math.PI;
        if (rotDeg < 0) {
            // Create range of 0 to 360 degrees instead of -180 to 180
            rotDeg = 360 - Math.abs(rotDeg);
        }
        this.rotation = -rotDeg + this.fixedRotation;
    }

    public getPosition() {
        return this.position;
    }

    public getBoundingBox(): BoundingBox {
        let min = vec3.subtract(vec3.create(), this.position, [2, 2, 2]);
        let max = vec3.add(vec3.create(), this.position, [2, 2, 2]);

        return new BoundingBox(min, max);
    }

    public switchAttachment(boneReference: string, obj: RenderObject) {

        let node = this.visualScene.lookupNode(boneReference);
        if (!node) {
            throw new Error(`Attempted to attach object to bone ${boneReference} but such a bone does not exist.`);
        }

        this.jointAttachments.set(node, obj);
    }

    public removeAttachment(boneReference: string) {
        let node = this.visualScene.lookupNode(boneReference);
        if (node) {
            this.jointAttachments.delete(node);
        }
    }

    private getShaderSettings(picking: boolean): CompositeShaderSettings {
        let settings: CompositeShaderSettings = [];

        if (this.isAnimated) {
            settings.push('ANIMATION');
        }

        if (this.isAttached) {
            settings.push('ATTACHMENT');
        }

        if (picking) {
            settings.push('PICKING');
        } else {
            settings.push('DYNAMIC_LIGHTNING');
            if (this.texture) {
                settings.push('TEXTURE');
            } else {
                settings.push('ATTRIBUTE_COLOR', 'PRESET_COLOR');
            }
        }

        return settings;
    }

    public draw(ctx: Context, picking: boolean): void {
        //picking = !picking;
        let shader = ctx.engine.shaderManager.getCompositeShader(...this.getShaderSettings(picking));
        ctx.setShader2(shader);

        // Tempory hack
        if (this.loopUp) {
            this.loop += 0.01;
        } else {
            this.loop -= 0.01;
        }
        if (this.loop > 1) {
            this.loop = 1;
            this.loopUp = false;
        } else if (this.loop < 0) {
            this.loop = 0;
            this.loopUp = true;
        }

        if (this.drawCount++ % 2 === 0) {
            //this.frameIndex++;
        }

        let gl = ctx.gl;

        let mvMatrix = this.mvMatrix;

        if (!mvMatrix) {
            mvMatrix = mat4.create();
            mat4.copy(mvMatrix, ctx.modelViewMatrix);

            mat4.translate(mvMatrix, mvMatrix, this.position);
            mat4.scale(mvMatrix, mvMatrix, this.scale);
            if (this.rotation !== 0) {
                mat4.rotate(mvMatrix, mvMatrix, toRadian(this.rotation), this.rotationAxis);
            }
        }

        gl.uniformMatrix4fv(shader.uniforms.uProjectionMatrix, false, ctx.projectionMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uModelViewMatrix, false, mvMatrix);

        let vertexBuffer = this.mesh.buffer;

        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer.glVertexBuffer);
        gl.vertexAttribPointer(shader.attributes.aVertexPosition, 3, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * vertexBuffer.itemSize, 0);

        if (picking) {
            gl.uniform4fv(shader.uniforms.uPickingColor, vec4.fromValues((this.pickingColor & 0xFF) / 255, (this.pickingColor >> 8 & 0xFF) / 255, (this.pickingColor >> 16 & 0xFF) / 255, 1));

            if (this.isAnimated) {
                this.renderAnimationDataTexture(ctx, shader, vertexBuffer, mvMatrix);
            }
        } else {
            gl.vertexAttribPointer(shader.attributes.aVertexNormal, 3, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * vertexBuffer.itemSize, Float32Array.BYTES_PER_ELEMENT * 3);
            if (this.texture) {
                this.setupTexturedVertices(ctx, shader, vertexBuffer);
                this.renderLightning(ctx, shader, mvMatrix);
            } else {
                this.setupColoredVertices(ctx, shader, vertexBuffer);
                this.renderLightning(ctx, shader, mvMatrix);
            }

            if (this.isAnimated) {
                this.renderAnimationDataTexture(ctx, shader, vertexBuffer, mvMatrix);
            }

            if (this.isAttached) {
                gl.uniformMatrix4fv(shader.uniforms.uBoneMatrix, false, this.boneMatrix);
                gl.uniformMatrix4fv(shader.uniforms.uOffsetMatrix, false, this.offsetMatrix);
            }
        }

        gl.drawArrays(vertexBuffer.glDrawMode, 0, vertexBuffer.itemCount);


        //this.jointAttachments.forEach((obj, node) => obj.draw(ctx, !picking));

    }

    private renderLightning(ctx: Context, shader: CompositeShader, mvMatrix: mat4) {
        let gl = ctx.gl;
        let normalMatrix = mat3.normalFromMat4(mat3.create(), mvMatrix);
        gl.uniformMatrix3fv(shader.uniforms.uNormalMatrix, false, normalMatrix);

        let lightManager = ctx.engine.activeScene.lightManager;

        gl.uniform3fv(shader.uniforms.uAmbientColor, lightManager.ambientColor);

        gl.uniformMatrix4fv(shader.uniforms.uLightMatrix, false, ctx.modelViewMatrix);

        let lights = lightManager.getNearestLights(this.position);

        for (let i = 0; i < 8 && i < lights.length; i++) {
            gl.uniform4fv(shader.uniforms.uPointLightingColor[i], lights[i].color);
            gl.uniform4fv(shader.uniforms.uPointLightingLocation[i], lights[i].position);
        }
    }

    private renderAnimationDataTexture(ctx: Context, shader: CompositeShader, vertexBuffer: VertexBuffer, mvMatrix: mat4) {
        let gl = ctx.gl;

        gl.vertexAttribPointer(shader.attributes.aJointWeights, 4, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * vertexBuffer.itemSize, Float32Array.BYTES_PER_ELEMENT * 10);
        gl.vertexAttribPointer(shader.attributes.aJointIndices, 4, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * vertexBuffer.itemSize, Float32Array.BYTES_PER_ELEMENT * 14);

        // The bind shape matrix aligns the vertices to the right position to apply bone transformations to them
        gl.uniformMatrix4fv(shader.uniforms.uBindShapeMatrix, false, this.animation.bindShapeMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uInvBindShapeMatrix, false, this.animation.invBindShapeMatrix);

        let frameSet = this.animationState.getFrames();

        let offset = this.animation.dataTexture.getFrameOffset(frameSet.state, frameSet.frameIndex);

        let dimension = this.animation.dataTexture.dimension;
        gl.uniform3fv(shader.uniforms.uDataSettings, [dimension.width, dimension.height, offset]);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.animation.dataTexture.glTexture);
        gl.uniform1i(shader.uniforms.uMatrixSampler, 0); // bind texture0


        for (let i = 0; i < this.animation.joints.length; i++) {
            let joint = this.animation.joints[i];

            if (this.jointAttachments.has(joint)) {

                let attachment = this.jointAttachments.get(joint);

                let m = mat4.clone(mvMatrix);
                mat4.scale(m, m, this.scale);

                let offsetMatrix = mat4.create();
                // TODO: Add offset/rotation specific to attachment
                let boneMat = mat4.clone(frameSet.frames[i].jointMatrix);

                attachment.mvMatrix = m;
                attachment.isAttached = true;
                attachment.boneMatrix = boneMat;
                attachment.offsetMatrix = offsetMatrix;
            }
        }
    }

    private setupColoredVertices(ctx: Context, shader: CompositeShader, vertexBuffer: VertexBuffer) {
        let gl = ctx.gl;
        gl.vertexAttribPointer(shader.attributes.aVertexColor, 4, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * vertexBuffer.itemSize, Float32Array.BYTES_PER_ELEMENT * 6);

        let color = this.baseColor;
        if (this.selected && this.hovered) {
            color = vec4.fromValues(1, 0, 0, 1);
        } else if (this.selected) {
            color = vec4.fromValues(0.5 * this.loop, 0.2 * this.loop, 1 * this.loop, 1);
        } else if (this.hovered) {
            color = vec4.fromValues(1, 1, 0, 1);
        }
        gl.uniform4fv(shader.uniforms.uPresetColor, color);
    }

    private setupTexturedVertices(ctx: Context, shader: CompositeShader, vertexBuffer: VertexBuffer) {
        let gl = ctx.gl;
        gl.vertexAttribPointer(shader.attributes.aTextureCoord, 2, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * vertexBuffer.itemSize, Float32Array.BYTES_PER_ELEMENT * 6);

        let color = vec4.fromValues(1, 1, 1, 1);
        if (this.selected && this.hovered) {
            color = vec4.fromValues(1, 0, 0, 1);
        } else if (this.selected) {
            color = vec4.fromValues(0.5 * this.loop, 0.2 * this.loop, 1 * this.loop, 1);
        } else if (this.hovered) {
            color = vec4.fromValues(1, 1, 0, 1);
        }
        gl.uniform4fv(shader.uniforms.uPresetColor, color);

        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.texture.glTexture);
        gl.uniform1i(shader.uniforms.uTextureSampler, 1); // bind texture1
    }

    public getModes(): string[] {
        return ['main', 'picking'];
    }

    public getShaderReference(mode: string): string {
        return this.getShaderSettings(mode == 'picking').join();
    }
}